import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";
import { DEFAULT_LANG } from "@/i18n";
import { TemplateQuestionsManager } from "./templates/TemplateQuestionsManager";
import TemplateBasicInfo from "./templates/TemplateBasicInfo";
import TemplateTranslations from "./templates/TemplateTranslations";
import TemplateAdditionalSettings from "./templates/TemplateAdditionalSettings";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLanguages } from "@/hooks/useLanguages";

interface SurveyTemplate {
  id: string;
  name: string;
  description: string | null;
  category: string;
  tool_id: string;
  is_featured: boolean;
  estimated_duration_minutes: number | null;
  target_audience: string | null;
}

interface MinimalToolOption {
  id: string;
  name: string;
}

interface SurveyTemplateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  template: SurveyTemplate | null;
  onSuccess: () => void;
}

export const SurveyTemplateDialog = ({
  isOpen,
  onClose,
  template,
  onSuccess,
}: SurveyTemplateDialogProps) => {
  const { i18n } = useTranslation();
  const { data: languages } = useLanguages();
  const [activeLang, setActiveLang] = useState<string>(i18n.language || DEFAULT_LANG);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "",
    tool_id: "",
    is_featured: false,
    estimated_duration_minutes: "",
    target_audience: "",
  });

  const [nameI18n, setNameI18n] = useState<Record<string, string>>({});
  const [descI18n, setDescI18n] = useState<Record<string, string>>({});

  const { data: tools } = useQuery({
    queryKey: ['tools-for-templates'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tools')
        .select('id, name')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      return data as MinimalToolOption[];
    },
  });

  // Initialize form when template changes
  useEffect(() => {
    if (template) {
      // Parse i18n fields safely
      let nameI18n = {};
      let descI18n = {};
      
      try {
        const t: any = template as any;
        if (t.name_i18n) {
          nameI18n = typeof t.name_i18n === 'string' 
            ? JSON.parse(t.name_i18n) 
            : t.name_i18n;
        }
        if (t.description_i18n) {
          descI18n = typeof t.description_i18n === 'string'
            ? JSON.parse(t.description_i18n)
            : t.description_i18n;
        }
      } catch (error) {
        console.error('Error parsing i18n fields:', error);
        nameI18n = {};
        descI18n = {};
      }

      setFormData({
        name: template.name,
        description: template.description || "",
        category: template.category,
        tool_id: template.tool_id,
        is_featured: template.is_featured,
        estimated_duration_minutes: template.estimated_duration_minutes?.toString() || "",
        target_audience: template.target_audience || "",
      });
      setNameI18n(nameI18n);
      setDescI18n(descI18n);
    }
  }, [template]);

  // Handle dialog state changes and cleanup
  useEffect(() => {
    if (!isOpen) {
      // Always reset when dialog closes
      setFormData({
        name: "",
        description: "",
        category: "",
        tool_id: "",
        is_featured: false,
        estimated_duration_minutes: "",
        target_audience: "",
      });
      setNameI18n({});
      setDescI18n({});
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const submitData: any = {
      name: formData.name,
      description: formData.description || null,
      category: formData.category,
      tool_id: formData.tool_id,
      is_featured: formData.is_featured,
      estimated_duration_minutes: formData.estimated_duration_minutes
        ? parseInt(formData.estimated_duration_minutes)
        : null,
      target_audience: formData.target_audience || null,
      name_i18n: nameI18n,
      description_i18n: descI18n,
    };

    try {
      if (template) {
        // Update existing template
        const { error } = await supabase
          .from('survey_templates')
          .update(submitData)
          .eq('id', template.id);
        if (error) throw error;
      } else {
        // Create new template
        const { error } = await supabase
          .from('survey_templates')
          .insert(submitData);
        if (error) throw error;
      }

      onSuccess();
    } catch (error) {
      console.error('Error saving survey template:', error);
    }
  };

  // TODO: get categories from the database
  const categories = [
    "general", "leadership", "diversity", "teamwork", "remote_work", 
    "communication", "performance", "development", "engagement", "feedback", "culture"
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {template ? "Edit Survey Template" : "Add Survey Template"}
          </DialogTitle>
          <DialogDescription>
            {template
              ? "Update the survey template information."
              : "Create a new survey template for organizing questions."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <TemplateBasicInfo
            name={formData.name}
            onNameChange={(v) => setFormData({ ...formData, name: v })}
            category={formData.category}
            onCategoryChange={(v) => setFormData({ ...formData, category: v })}
            description={formData.description}
            onDescriptionChange={(v) => setFormData({ ...formData, description: v })}
            categories={categories}
          />

          <TemplateTranslations
            languages={languages as any}
            activeLang={activeLang}
            onActiveLangChange={(v) => setActiveLang(v)}
            nameI18n={nameI18n}
            onNameChange={(lang, value) => setNameI18n({ ...nameI18n, [lang]: value })}
            descI18n={descI18n}
            onDescChange={(lang, value) => setDescI18n({ ...descI18n, [lang]: value })}
          />

          <TemplateAdditionalSettings
            tools={tools as any}
            toolId={formData.tool_id}
            onToolChange={(v) => setFormData({ ...formData, tool_id: v })}
            estimatedMinutes={formData.estimated_duration_minutes}
            onEstimatedMinutesChange={(v) => setFormData({ ...formData, estimated_duration_minutes: v })}
            targetAudience={formData.target_audience}
            onTargetAudienceChange={(v) => setFormData({ ...formData, target_audience: v })}
            isFeatured={formData.is_featured}
            onFeaturedChange={(checked) => setFormData({ ...formData, is_featured: checked })}
          />

          <div className="flex justify-end space-x-3 pt-6 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">
              {template ? "Update" : "Create"} Template
            </Button>
          </div>
        </form>

        {template?.id ? (
          <div className="mt-8 pt-6 border-t">
            <Label className="mb-4 block text-lg font-medium">Manage Questions</Label>
            <TemplateQuestionsManager templateId={template.id} />
          </div>
        ) : (
          <div className="mt-8 pt-6 border-t text-sm text-muted-foreground">
            Save the template first to manage its questions.
          </div>
        )}

      </DialogContent>
    </Dialog>
  );
};